import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Zap, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

function trackEvent(authAxios, event, feature) {
  authAxios.post("/analytics/track", { event, feature }).catch(() => {});
}

/**
 * Displays a locked state for premium features.
 * Shows CTA to upgrade plan or buy from marketplace.
 */
export function LockedFeature({ featureName, description, addonId }) {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  // Track view once on mount
  useEffect(() => {
    if (addonId) trackEvent(authAxiosRef.current, "feature_locked_view", addonId);
  }, [addonId]);

  const handleUpgradeClick = () => {
    trackEvent(authAxios, "upgrade_clicked", addonId);
    navigate("/settings?tab=subscription");
  };

  const handleMarketplaceClick = () => {
    trackEvent(authAxios, "marketplace_clicked", addonId);
    navigate("/marketplace");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
        <Lock className="w-8 h-8 text-amber-600" />
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {featureName} — Recurso Premium
      </h2>

      <p className="text-gray-500 max-w-md mb-2 text-sm leading-relaxed">
        {description ||
          "Você está visualizando uma prévia deste módulo. Para continuar utilizando análises avançadas e inteligência em tempo real, escolha uma das opções abaixo:"}
      </p>

      <p className="text-xs text-gray-400 mb-8 max-w-sm">
        Este recurso estava disponível no período de teste. Para continuar utilizando,
        faça upgrade para o plano completo ou adquira separadamente no marketplace.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleUpgradeClick}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 px-6"
        >
          <Zap className="w-4 h-4" />
          Ativar Plano Completo
        </Button>
        <Button
          variant="outline"
          onClick={handleMarketplaceClick}
          className="gap-2 px-6"
        >
          <ShoppingBag className="w-4 h-4" />
          Comprar no Marketplace
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg text-xs text-gray-400">
        {[
          { label: "Antifurto IA", price: "€29/mês" },
          { label: "Analytics Cozinha", price: "€19/mês" },
          { label: "IA Operacional", price: "€39/mês" },
          { label: "IA Executiva", price: "€59/mês" },
          { label: "Kaiso Brain", price: "€49/mês" },
          { label: "Plano Completo", price: "€189/mês" },
        ].map((item) => (
          <div key={item.label} className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="font-medium text-gray-600">{item.label}</p>
            <p className="text-emerald-600 font-bold">{item.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
